import { showNotification } from "@mantine/notifications"
import { IconDownload, IconTrash, IconUpload } from "@tabler/icons-react"
import { ButtonWithMenu, ButtonWithMenuProps } from "@tfg/ui"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { queryClient } from "~/backend/queryClient"
import {
  FindProductsCatalogRequestFiltersDto,
  generateExportByKeyRequestDtoKeyEnum,
  useGenerateExportByKey,
} from "~/gen/api"

export type CatalogActionsMenuProps = {
  selectedProductIds: string[]
  filters: FindProductsCatalogRequestFiltersDto
  onProductsDeleteClick: () => void
  isProductsDeleteLoading: boolean
}

export const CatalogActionsMenu = ({
  selectedProductIds,
  filters,
  onProductsDeleteClick,
  isProductsDeleteLoading,
}: CatalogActionsMenuProps) => {
  const { t } = useTranslation(["common", "products", "dataTransfer"])
  const navigate = useNavigate()

  const { mutate: generateExportByKey, isPending: isCatalogExportPending } =
    useGenerateExportByKey()

  const onCatalogExportClick = useCallback(() => {
    generateExportByKey(
      {
        data: {
          key: generateExportByKeyRequestDtoKeyEnum.EXPORT_CATALOG,
          filters,
        },
      },
      {
        onSuccess() {
          showNotification({
            color: "green",
            message: t("dataTransfer:export.notifications.generate.success"),
          })

          queryClient.invalidateQueries({
            queryKey: [{ url: "/v1/export/export-requests" }],
          })

          // Navigate only after the export job has been queued so the
          // requests table can already show the new export.
          navigate("/data-transfer/export")
        },
      }
    )
  }, [filters, generateExportByKey, navigate, t])

  const isMenuLoading = isProductsDeleteLoading || isCatalogExportPending

  const items = useMemo<ButtonWithMenuProps["items"]>(() => {
    const result = [
      {
        icon: <IconDownload />,
        label: `${t("common:actionButtons.export")}`,
        onClick: onCatalogExportClick,
        disabled: isCatalogExportPending,
      },
      {
        icon: <IconTrash />,
        label: `${t("products:bulkActions.delete.action.name", { count: selectedProductIds.length })}`,
        onClick: onProductsDeleteClick,
        disabled: selectedProductIds.length === 0 || isCatalogExportPending,
      },
      {
        icon: <IconUpload />,
        label: `${t("products:bulkActions.import.actionName")}`,
        onClick: () => navigate("/data-transfer/import"),
        disabled: isCatalogExportPending,
      },
    ]

    return result
  }, [
    isCatalogExportPending,
    navigate,
    onCatalogExportClick,
    onProductsDeleteClick,
    selectedProductIds,
    t,
  ])

  return (
    <ButtonWithMenu
      loading={isMenuLoading}
      items={items}
      disabled={isMenuLoading}
    >
      {t("products:bulkActions.title")}
    </ButtonWithMenu>
  )
}
